import { config } from "@config/config";
import { UseQueryResult, keepPreviousData } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { isSyncDone } from "@utils/learningTreeReview";
import { unwrapConnection } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { debounce } from "lodash";
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  ApiLearnerManualGoalSyncStatusChoices,
  ClientProgrammingProviderQuery,
  GetReportsQuery,
  LearnerManualGoalSyncByIdQuery,
  ReportTypeEnums,
  useClientProgrammingProviderQuery,
  useGetReportsQuery,
  useLearnerManualGoalSyncByIdQuery,
} from "@api/graphql/types-and-hooks";

// Regular programming types
type GoalNode = NonNullable<NonNullable<NonNullable<ClientProgrammingProviderQuery["goals"]>["edges"][number]>["node"]>;
export type Target = NonNullable<NonNullable<GoalNode["targets"]["edges"][number]>["node"]>;
export type Goal = Omit<GoalNode, "targets"> & { targets: Array<Target> | null };
export type GoalList = Array<Goal> | null | undefined;

export const navigationTabs = ["Overview", "Video Moments"] as const;
type NavigationTab = (typeof navigationTabs)[number];

interface ClientProgrammingContextI {
  getClientGoalDataQuery: UseQueryResult<ClientProgrammingProviderQuery, unknown>;
  goalList: GoalList;
  setSelectedGoalId: (goalId: string | null) => void;
  setSelectedTargetId: (targetId: string | null) => void;
  selectedGoal: Goal | null;
  selectedTarget: Target | null;
  selectedProgrammingGoalTab: NavigationTab;
  setSelectedProgrammingGoalTab: React.Dispatch<React.SetStateAction<NavigationTab>>;
  learnerId: string | undefined;
  searchGoalText: string;
  setSearchGoalText: (value: string | ((prev: string) => string)) => void;
  selectedGoalStatus: string | undefined;
  setSelectedGoalStatus: React.Dispatch<React.SetStateAction<string | undefined>>;
  currentlyPlayingClipID: string | null;
  setCurrentlyPlayingClipID: React.Dispatch<React.SetStateAction<string | null>>;
  goalRefreshStatus: string;
  goalRefreshLastUpdated: string;
  goalRefreshLoading: boolean;
  setGoalRefreshLoading: React.Dispatch<React.SetStateAction<boolean>>;
  goalRefreshStatusQuery: UseQueryResult<LearnerManualGoalSyncByIdQuery, unknown>;
  isTreatmentPlanUploadEnabled: boolean;
  latestInitialAssessmentReportQuery: UseQueryResult<GetReportsQuery, unknown>;
}

const ClientProgrammingContext = createContext<ClientProgrammingContextI | undefined>(undefined);

export const useClientProgrammingData = () => {
  const context = useContext(ClientProgrammingContext);
  if (context === undefined) {
    throw new Error("useClientProgrammingData must be used within a ClientProgrammingProvider");
  }
  return context;
};

export const ClientProgrammingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { clientId, goalId, targetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const flags = useFlags<FeatureFlags>();

  const [selectedGoalId, setSelectedGoalIdInternal] = useState<string | null>(null);
  const [selectedTargetId, setSelectedTargetIdInternal] = useState<string | null>(null);
  const [selectedProgrammingGoalTab, setSelectedProgrammingGoalTab] = useState<NavigationTab>("Overview");
  const [selectedGoalStatus, setSelectedGoalStatus] = useState<string>();
  const [currentlyPlayingClipID, setCurrentlyPlayingClipID] = useState<string | null>(null);

  const [goalRefreshStatus, setGoalRefreshStatus] = useState<string>("");
  const [goalRefreshLastUpdated, setGoalRefreshLastUpdated] = useState<string>("");
  const [goalRefreshLoading, setGoalRefreshLoading] = useState<boolean>(false);

  const prevStatusRef = useRef<string>("");

  // Regular programming query
  const getClientGoalDataQuery = useClientProgrammingProviderQuery(
    { learnerId: clientId!, filter: { goalStatus: selectedGoalStatus } },
    {
      enabled: !!clientId,
      queryKey: ["goals", clientId, selectedGoalStatus],
      placeholderData: keepPreviousData,
    },
  );

  const { data, refetch: refetchGoals } = getClientGoalDataQuery;

  const latestInitialAssessmentReportQuery = useGetReportsQuery(
    {
      first: 1,
      filter: {
        learnerId: clientId || "",
        reportType: ReportTypeEnums.InitialAssessment,
        sortBy: "-created_date",
      },
    },
    {
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const goalRefreshStatusQuery = useLearnerManualGoalSyncByIdQuery(
    { learnerId: clientId || "" },
    {
      refetchInterval: (query) => {
        const status = query.state?.data?.learnerById?.manualGoalSyncStatus;
        if (status === ApiLearnerManualGoalSyncStatusChoices.Inprogress) {
          return config.REPORT_POLLING_INTERVAL;
        }
        return false;
      },
    },
  );

  const { data: goalRefreshStatusData } = goalRefreshStatusQuery;

  useEffect(() => {
    const manualSyncStatus = goalRefreshStatusData?.learnerById?.manualGoalSyncStatus;
    const lastUpdated = goalRefreshStatusData?.learnerById?.manualGoalSyncLastUpdated;

    setGoalRefreshStatus(manualSyncStatus || "");
    setGoalRefreshLastUpdated(lastUpdated || "");
    if (
      manualSyncStatus &&
      (isSyncDone(manualSyncStatus) || manualSyncStatus === ApiLearnerManualGoalSyncStatusChoices.Failure) &&
      prevStatusRef.current === ApiLearnerManualGoalSyncStatusChoices.Inprogress
    ) {
      setGoalRefreshLoading(false);
      if (isSyncDone(manualSyncStatus)) {
        refetchGoals();
      }
    }
    prevStatusRef.current = manualSyncStatus || "";
  }, [goalRefreshStatusQuery.data]);
  // Search functionality
  const searchGoalText = searchParams.get("search") || "";
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const debouncedUpdateSearchParams = useCallback(
    debounce((value: string) => {
      if (value.trim()) {
        setSearchParams({ search: value }, { replace: true });
      } else {
        const newSearchParams = new URLSearchParams(searchParamsRef.current);
        newSearchParams.delete("search");
        setSearchParams(newSearchParams, { replace: true });
      }
    }, 100),
    [setSearchParams],
  );

  const setSearchGoalText = (value: string | ((prev: string) => string)) => {
    const newValue = typeof value === "function" ? value(searchGoalText) : value;
    debouncedUpdateSearchParams(newValue);
  };

  // Process goals
  const goalList: GoalList = useMemo(() => {
    const goals = unwrapConnection(data?.goals);
    return goals?.map((g) => {
      const { targets, ...rest } = g;
      return { ...rest, targets: unwrapConnection(targets) };
    });
  }, [data]);

  // Build lookup tables
  const [goalById, targetById, goalIdByTargetId] = useMemo(() => {
    const goalById: Record<string, Goal> = {};
    const targetById: Record<string, Target> = {};
    const goalIdByTargetId: Record<string, string> = {};

    for (const goal of goalList || []) {
      goalById[goal.id] = goal;
      for (const target of goal.targets || []) {
        targetById[target.id] = target;
        goalIdByTargetId[target.id] = goal.id;
      }
    }
    return [goalById, targetById, goalIdByTargetId];
  }, [goalList]);

  // Selection handlers
  const setSelectedGoalId = (newGoalId: string | null) => {
    setSelectedGoalIdInternal(newGoalId || null);
    setSelectedTargetIdInternal(null);
  };

  const setSelectedTargetId = (newTargetId: string | null) => {
    if (newTargetId) {
      setSelectedTargetIdInternal(newTargetId);
      setSelectedGoalIdInternal(goalIdByTargetId[newTargetId] || null);
    } else {
      setSelectedGoalIdInternal(null);
      setSelectedTargetIdInternal(null);
    }
  };

  // Sync URL params with state
  useEffect(() => {
    if (goalId && goalList?.length) {
      const goal = goalList.find((g) => g.id === goalId);

      if (goal) {
        if (targetId) {
          const target = goal.targets?.find((t) => t.id === targetId);
          if (target) {
            setSelectedTargetIdInternal(targetId);
            setSelectedGoalIdInternal(goalId);
          }
        } else {
          setSelectedGoalIdInternal(goalId);
          setSelectedTargetIdInternal(null);
        }
      }
    } else if (!goalId) {
      setSelectedGoalIdInternal(null);
      setSelectedTargetIdInternal(null);
    }
  }, [goalId, targetId, goalList]);

  // Derived selected values
  const selectedGoal = selectedGoalId ? goalById[selectedGoalId] : null;
  const selectedTarget = selectedTargetId ? targetById[selectedTargetId] : null;

  return (
    <ClientProgrammingContext.Provider
      value={{
        goalList,
        setSelectedGoalId,
        setSelectedTargetId,
        getClientGoalDataQuery,
        selectedGoal,
        selectedTarget,
        selectedProgrammingGoalTab,
        setSelectedProgrammingGoalTab,
        learnerId: clientId,
        searchGoalText,
        setSearchGoalText,
        selectedGoalStatus,
        setSelectedGoalStatus,
        currentlyPlayingClipID,
        setCurrentlyPlayingClipID,
        goalRefreshStatus,
        goalRefreshLastUpdated,
        goalRefreshLoading,
        setGoalRefreshLoading,
        goalRefreshStatusQuery,
        isTreatmentPlanUploadEnabled: flags["treatment-plan-upload"],
        latestInitialAssessmentReportQuery,
      }}
    >
      {children}
    </ClientProgrammingContext.Provider>
  );
};
